import { Add as AddIcon } from "@mui/icons-material";
import { TabContext, TabList, TabPanel } from "@mui/lab";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Tab,
  TextField,
  Tooltip,
} from "@mui/material";
import type { FC } from "react";
import { useEffect, useState } from "react";
import AsyncLoadingButton from "../asyncLoadingButton/AsyncLoadingButton";
import LocalFilePicker from "./LocalFilePicker";

const AddMode = {
  LINK: "LINK",
  DISK: "DISK",
  CLIPBOARD: "CLIPBOARD",
};

interface Props {
  readonly buttonTooltip?: string;
  readonly hideFilename?: boolean;
  readonly onNewFile: (formData: FormData) => void | Promise<void>;
}

export const AssociatedFilesDialog: FC<Props> = ({ buttonTooltip = "Dodaj załącznik", hideFilename, onNewFile }) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [mode, setMode] = useState(AddMode.LINK);
  const [filename, setFilename] = useState("");
  const [fileUrl, setFileUrl] = useState("");
  const [file, setFile] = useState<File | undefined>(undefined);
  const [clipboardImage, setClipboardImage] = useState<File | undefined>(undefined);
  const [clipboardPreviewUrl, setClipboardPreviewUrl] = useState<string | undefined>(undefined);

  useEffect(() => {
    setFilename("");
    setFileUrl("");
    setClipboardImage(undefined);
    setClipboardPreviewUrl(undefined);
  }, [dialogOpen, mode]);

  useEffect(() => {
    return () => {
      if (clipboardPreviewUrl) {
        URL.revokeObjectURL(clipboardPreviewUrl);
      }
    };
  }, [clipboardPreviewUrl]);

  const handlePasteFromClipboard = async () => {
    const items = await navigator.clipboard.read();
    for (const item of items) {
      const imageType = item.types.find((type) => type.startsWith("image/"));
      if (imageType) {
        const blob = await item.getType(imageType);
        const extension = imageType.split("/")[1];
        const pastedFile = new File([blob], `clipboard.${extension}`, { type: imageType });
        const url = URL.createObjectURL(pastedFile);
        setClipboardImage(pastedFile);
        setClipboardPreviewUrl(url);
        break;
      }
    }
  };

  const handleAdd = async () => {
    const formData = new FormData();
    if (!hideFilename) {
      formData.append("name", filename);
    }
    if (mode === AddMode.LINK) {
      formData.append("url", fileUrl);
    } else if (mode === AddMode.DISK && file) {
      formData.append("file", file, file.name);
    } else if (mode === AddMode.CLIPBOARD && clipboardImage) {
      formData.append("file", clipboardImage, clipboardImage.name);
    } else {
      throw new Error(`File is missing for mode ${mode}`);
    }
    await onNewFile(formData);
    setDialogOpen(false);
  };

  const addEnabled =
    (hideFilename || filename.length > 0) &&
    ((mode === AddMode.LINK && fileUrl.length > 0) ||
      (mode === AddMode.DISK && !!file) ||
      (mode === AddMode.CLIPBOARD && !!clipboardImage));
  return (
    <>
      <Tooltip title={buttonTooltip}>
        <IconButton onClick={() => setDialogOpen(true)}>
          <AddIcon />
        </IconButton>
      </Tooltip>
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)}>
        <DialogTitle>Załącz plik</DialogTitle>
        <DialogContent>
          <TabContext value={mode}>
            <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
              <TabList onChange={(_, newValue) => setMode(newValue)}>
                <Tab label="Dodaj link" value={AddMode.LINK} />
                <Tab label="Wybierz z dysku" value={AddMode.DISK} />
                <Tab label="Ze schowka" value={AddMode.CLIPBOARD} />
              </TabList>
            </Box>
            <TabPanel value={AddMode.LINK}>
              <Stack
                spacing={2}
                sx={{
                  minWidth: "400px",
                }}
              >
                {!hideFilename && (
                  <TextField label="Nazwa" fullWidth value={filename} onChange={(e) => setFilename(e.target.value)} />
                )}
                <TextField
                  label="Adres URL"
                  type="url"
                  fullWidth
                  value={fileUrl}
                  onChange={(e) => setFileUrl(e.target.value)}
                />
              </Stack>
            </TabPanel>
            <TabPanel value={AddMode.DISK}>
              <Stack
                spacing={2}
                sx={{
                  minWidth: "400px",
                }}
              >
                {!hideFilename && (
                  <TextField label="Nazwa" fullWidth value={filename} onChange={(e) => setFilename(e.target.value)} />
                )}
                <LocalFilePicker onFileChange={setFile} />
              </Stack>
            </TabPanel>
            <TabPanel value={AddMode.CLIPBOARD}>
              <Stack
                spacing={2}
                sx={{
                  minWidth: "400px",
                }}
              >
                {!hideFilename && (
                  <TextField label="Nazwa" fullWidth value={filename} onChange={(e) => setFilename(e.target.value)} />
                )}
                <Button variant="outlined" onClick={handlePasteFromClipboard}>
                  Wklej ze schowka
                </Button>
                {clipboardPreviewUrl && (
                  <Box
                    component="img"
                    src={clipboardPreviewUrl}
                    alt="Podgląd"
                    sx={{ maxWidth: "100%", maxHeight: 300, objectFit: "contain" }}
                  />
                )}
              </Stack>
            </TabPanel>
          </TabContext>
        </DialogContent>
        <DialogActions>
          <Button color="secondary" onClick={() => setDialogOpen(false)}>
            Anuluj
          </Button>
          <AsyncLoadingButton color="primary" disabled={!addEnabled} onClick={handleAdd}>
            Dodaj
          </AsyncLoadingButton>
        </DialogActions>
      </Dialog>
    </>
  );
};
